/**
 * Layout Algorithms — Phase 2
 * Compute positions for sets of items in various arrangements.
 */

import type { PlacementResult } from './canvasPlacement';

interface LayoutItem {
  id: string;
  width: number;
  height: number;
}

/** Grid layout — organized rows and columns */
export function computeGridLayout(
  items: LayoutItem[],
  options: {
    columns?: number;
    spacing?: number;
    startX?: number;
    startY?: number;
  } = {}
): Map<string, PlacementResult> {
  const {
    columns = Math.ceil(Math.sqrt(items.length)),
    spacing = 40,
    startX = 0,
    startY = 0,
  } = options;

  const results = new Map<string, PlacementResult>();

  const maxWidth = Math.max(...items.map(i => i.width));
  const maxHeight = Math.max(...items.map(i => i.height));

  items.forEach((item, index) => {
    const col = index % columns;
    const row = Math.floor(index / columns);

    results.set(item.id, {
      x: startX + col * (maxWidth + spacing) + (maxWidth - item.width) / 2,
      y: startY + row * (maxHeight + spacing) + (maxHeight - item.height) / 2,
    });
  });

  return results;
}

/** Horizontal row layout */
export function computeHorizontalLayout(
  items: LayoutItem[],
  options: {
    spacing?: number;
    startX?: number;
    startY?: number;
    alignY?: 'top' | 'center' | 'bottom';
  } = {}
): Map<string, PlacementResult> {
  const { spacing = 40, startX = 0, startY = 0, alignY = 'center' } = options;
  const results = new Map<string, PlacementResult>();
  const maxHeight = Math.max(...items.map(i => i.height));

  let currentX = startX;
  items.forEach(item => {
    let y = startY;
    if (alignY === 'center') y = startY + (maxHeight - item.height) / 2;
    if (alignY === 'bottom') y = startY + maxHeight - item.height;

    results.set(item.id, { x: currentX, y });
    currentX += item.width + spacing;
  });

  return results;
}

/** Vertical column layout */
export function computeVerticalLayout(
  items: LayoutItem[],
  options: {
    spacing?: number;
    startX?: number;
    startY?: number;
    alignX?: 'left' | 'center' | 'right';
  } = {}
): Map<string, PlacementResult> {
  const { spacing = 40, startX = 0, startY = 0, alignX = 'center' } = options;
  const results = new Map<string, PlacementResult>();
  const maxWidth = Math.max(...items.map(i => i.width));

  let currentY = startY;
  items.forEach(item => {
    let x = startX;
    if (alignX === 'center') x = startX + (maxWidth - item.width) / 2;
    if (alignX === 'right') x = startX + maxWidth - item.width;

    results.set(item.id, { x, y: currentY });
    currentY += item.height + spacing;
  });

  return results;
}

/** Radial / mind map layout */
export function computeRadialLayout(
  centerItem: LayoutItem,
  childItems: LayoutItem[],
  options: {
    radius?: number;
    startAngle?: number;
    centerX?: number;
    centerY?: number;
  } = {}
): Map<string, PlacementResult> {
  const {
    radius = 250,
    startAngle = -Math.PI / 2,
    centerX = 0,
    centerY = 0,
  } = options;

  const results = new Map<string, PlacementResult>();

  results.set(centerItem.id, {
    x: centerX - centerItem.width / 2,
    y: centerY - centerItem.height / 2,
  });

  if (childItems.length === 0) return results;

  const angleStep = (2 * Math.PI) / childItems.length;

  childItems.forEach((item, index) => {
    const angle = startAngle + index * angleStep;
    results.set(item.id, {
      x: centerX + Math.cos(angle) * radius - item.width / 2,
      y: centerY + Math.sin(angle) * radius - item.height / 2,
    });
  });

  return results;
}

/** Cluster / organic grouping layout */
export function computeClusterLayout(
  items: LayoutItem[],
  options: {
    centerX?: number;
    centerY?: number;
    spread?: number;
  } = {}
): Map<string, PlacementResult> {
  const { centerX = 0, centerY = 0, spread = 60 } = options;
  const results = new Map<string, PlacementResult>();

  const angleStep = (2 * Math.PI) / items.length;
  const baseRadius = Math.max(100, items.length * spread / (2 * Math.PI));

  // Use seeded pseudo-random for deterministic jitter
  let seed = items.length * 17;
  const pseudoRandom = () => {
    seed = (seed * 16807 + 0) % 2147483647;
    return (seed - 1) / 2147483646;
  };

  items.forEach((item, index) => {
    const angle = index * angleStep + (pseudoRandom() - 0.5) * 0.3;
    const radius = baseRadius + (pseudoRandom() - 0.5) * spread;

    results.set(item.id, {
      x: centerX + Math.cos(angle) * radius - item.width / 2,
      y: centerY + Math.sin(angle) * radius - item.height / 2,
    });
  });

  return results;
}
